import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import {
  formatOdds,
  americanToDecimal,
  decimalToAmerican,
  getOddsFormat,
  setOddsFormat as saveOddsFormat
} from '../utils/oddsUtils';

function PromoConversionCalculator() {
  const location = useLocation();
  const passedData = location.state;

  const [oddsFormat, setOddsFormat] = useState(getOddsFormat());
  const [bonusBetAmount, setBonusBetAmount] = useState(100);
  const [bonusOdds, setBonusOdds] = useState('');
  const [hedgeBetAmount, setHedgeBetAmount] = useState(280);
  const [hedgeOdds, setHedgeOdds] = useState('');
  const [showDescription, setShowDescription] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);

  const handleOddsFormatChange = (newFormat) => {
    // Convert current odds to decimal first
    const decimalBonus = oddsFormat === 'American' ? americanToDecimal(bonusOdds) : parseFloat(bonusOdds) || 2.0;
    const decimalHedge = oddsFormat === 'American' ? americanToDecimal(hedgeOdds) : parseFloat(hedgeOdds) || 2.0;

    // Convert to new format
    if (newFormat === 'American') {
      setBonusOdds(decimalToAmerican(decimalBonus));
      setHedgeOdds(decimalToAmerican(decimalHedge));
    } else {
      setBonusOdds(decimalBonus.toFixed(2));
      setHedgeOdds(decimalHedge.toFixed(2));
    }

    setOddsFormat(newFormat);
    saveOddsFormat(newFormat);
  };

  useEffect(() => {
    if (passedData) {
      const format = getOddsFormat();
      setBonusOdds(formatOdds(passedData.bonusOdds || 2, format));
      setHedgeOdds(formatOdds(passedData.hedgeOdds || 2, format));
      setBonusBetAmount(passedData.bonusBetSize || 100);
      setHedgeBetAmount(passedData.hedgeBetSize || 100);
    } else {
      const format = getOddsFormat();
      setBonusOdds(format === 'American' ? '+350' : '4.50');
      setHedgeOdds(format === 'American' ? '-400' : '1.25');
    }
  }, [passedData]);

  // Convert display odds to decimal for calculations
  const getDecimalOdds = (displayOdds) => {
    if (oddsFormat === 'American') {
      return americanToDecimal(displayOdds);
    }
    return parseFloat(displayOdds) || 2.0;
  };

  const decimalBonusOdds = getDecimalOdds(bonusOdds);
  const decimalHedgeOdds = getDecimalOdds(hedgeOdds);

  const payout1 = (Number(bonusBetAmount) * decimalBonusOdds) - Number(bonusBetAmount);
  const stake1 = Number(hedgeBetAmount);
  const profit1 = payout1 - stake1;
  const conversion1 = (profit1 / Number(bonusBetAmount)) * 100;

  const payout2 = Number(hedgeBetAmount) * decimalHedgeOdds;
  const stake2 = Number(hedgeBetAmount);
  const profit2 = payout2 - stake2;
  const conversion2 = (profit2 / Number(bonusBetAmount)) * 100;

  const getBorderColor = (value) => {
    if (value > -0.01 && value < 0.01) return '#4a5568';
    return value > 0 ? '#10b981' : '#fc8181';
  };

  const getTextColor = (value) => {
    if (value > -0.01 && value < 0.01) return '#a0aec0';
    return value > 0 ? '#10b981' : '#f87171';
  };

  return (
    <div style={{
      backgroundColor: '#0a0e1a',
      minHeight: '100vh',
      padding: '40px 60px'
    }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h1 style={{
            fontSize: '36px',
            fontWeight: '800',
            margin: 0,
            color: '#ffffff',
            letterSpacing: '-0.5px'
          }}>
            Bonus Bet Calculator
          </h1>

          {/* Odds Format Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{
              fontSize: '14px',
              fontWeight: '600',
              color: oddsFormat === 'American' ? '#ffffff' : '#64748b',
              transition: 'color 0.2s'
            }}>
              American
            </span>
            <div
              onClick={() => handleOddsFormatChange(oddsFormat === 'American' ? 'Decimal' : 'American')}
              style={{
                width: '48px',
                height: '26px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #2d3748',
                borderRadius: '13px',
                cursor: 'pointer',
                position: 'relative',
                transition: 'all 0.2s'
              }}
            >
              <div style={{
                width: '20px',
                height: '20px',
                backgroundColor: '#00d9ff',
                borderRadius: '50%',
                position: 'absolute',
                top: '2px',
                left: oddsFormat === 'American' ? '3px' : '23px',
                transition: 'left 0.2s ease'
              }} />
            </div>
            <span style={{
              fontSize: '14px',
              fontWeight: '600',
              color: oddsFormat === 'Decimal' ? '#ffffff' : '#64748b',
              transition: 'color 0.2s'
            }}>
              Decimal
            </span>
          </div>
        </div>

        {/* Description */}
        <div style={{
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
          borderRadius: '8px',
          padding: '14px 20px',
          marginBottom: '10px'
        }}>
          <div
            onClick={() => setShowDescription(!showDescription)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            <h3 style={{
              margin: 0,
              fontSize: '15px',
              fontWeight: '600',
              color: '#ffffff'
            }}>
              Description
            </h3>
            <ChevronDown
              size={20}
              color="#a0aec0"
              style={{
                transition: 'transform 0.2s ease',
                transform: showDescription ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            />
          </div>
          {showDescription && (
            <div style={{
              fontSize: '15px',
              lineHeight: '1.7',
              color: '#a0aec0',
              margin: '15px 0 0 0'
            }}>
              <p style={{ margin: '0 0 10px 0' }}>
                Bonus bets can be converted to real cash by hedging the opposite outcome at another sportsbook.
              </p>
              <p style={{ margin: '0 0 10px 0' }}>
                Use this calculator to test conversion opportunities, explore different hedge amounts, or verify the math before placing bets.
              </p>
              <p style={{ margin: 0 }}>
                Remember: bonus bets don't return the stake, only the winnings. Both outcomes should show positive profit.
              </p>
            </div>
          )}
        </div>

        {/* How to Use */}
        <div style={{
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
          borderRadius: '8px',
          padding: '14px 20px',
          marginBottom: '20px'
        }}>
          <div
            onClick={() => setShowHowTo(!showHowTo)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            <h3 style={{
              margin: 0,
              fontSize: '15px',
              fontWeight: '600',
              color: '#ffffff'
            }}>
              How to Use
            </h3>
            <ChevronDown
              size={20}
              color="#a0aec0"
              style={{
                transition: 'transform 0.2s ease',
                transform: showHowTo ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            />
          </div>
          {showHowTo && (
            <div style={{
              fontSize: '15px',
              lineHeight: '1.7',
              color: '#a0aec0',
              margin: '15px 0 0 0'
            }}>
              <ol style={{ margin: 0, paddingLeft: '20px' }}>
                <li style={{ marginBottom: '8px' }}>Enter your bonus bet amount and the odds you're placing it at</li>
                <li style={{ marginBottom: '8px' }}>Enter your hedge bet amount and odds on the opposite outcome</li>
                <li style={{ marginBottom: '8px' }}>Adjust the hedge amount to balance profit across outcomes</li>
                <li>Aim for 70%+ conversion rate for a good bonus bet opportunity</li>
              </ol>
            </div>
          )}
        </div>

        {/* Calculator */}
        <div style={{
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
          borderRadius: '12px',
          padding: '30px'
        }}>
          {/* Two sides */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
            {/* Bonus Bet */}
            <div style={{
              backgroundColor: '#0f1419',
              padding: '24px',
              borderRadius: '10px',
              border: '1px solid #2d3748'
            }}>
              <h3 style={{
                color: '#ffffff',
                marginTop: 0,
                marginBottom: '20px',
                fontSize: '16px',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Bonus Bet
              </h3>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#a0aec0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Bet Amount ($)
                </label>
                <input
                  type="number"
                  value={bonusBetAmount}
                  onChange={(e) => setBonusBetAmount(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #2d3748',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '16px',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#00d9ff'}
                  onBlur={(e) => e.target.style.borderColor = '#2d3748'}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#a0aec0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Odds ({oddsFormat})
                </label>
                <input
                  type="text"
                  value={bonusOdds}
                  onChange={(e) => setBonusOdds(e.target.value)}
                  placeholder={oddsFormat === 'American' ? '+400' : '5.00'}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #2d3748',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '16px',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#00d9ff'}
                  onBlur={(e) => e.target.style.borderColor = '#2d3748'}
                />
              </div>
            </div>

            {/* Hedge Bet */}
            <div style={{
              backgroundColor: '#0f1419',
              padding: '24px',
              borderRadius: '10px',
              border: '1px solid #2d3748'
            }}>
              <h3 style={{
                color: '#ffffff',
                marginTop: 0,
                marginBottom: '20px',
                fontSize: '16px',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Hedge Bet
              </h3>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#a0aec0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Bet Amount ($)
                </label>
                <input
                  type="number"
                  value={hedgeBetAmount}
                  onChange={(e) => setHedgeBetAmount(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #2d3748',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '16px',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#00d9ff'}
                  onBlur={(e) => e.target.style.borderColor = '#2d3748'}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#a0aec0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Odds ({oddsFormat})
                </label>
                <input
                  type="text"
                  value={hedgeOdds}
                  onChange={(e) => setHedgeOdds(e.target.value)}
                  placeholder={oddsFormat === 'American' ? '-470' : '1.21'}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #2d3748',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '16px',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#00d9ff'}
                  onBlur={(e) => e.target.style.borderColor = '#2d3748'}
                />
              </div>
            </div>
          </div>

          {/* Results */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            {/* If Bonus Bet Wins */}
            <div style={{
              backgroundColor: '#0f1419',
              padding: '24px',
              borderRadius: '10px',
              border: `3px solid ${getBorderColor(profit1)}`,
              color: '#ffffff'
            }}>
              <div style={{
                textAlign: 'center',
                marginBottom: '20px',
                fontSize: '14px',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                If Bonus Bet Wins
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '12px',
                fontSize: '14px'
              }}>
                <span style={{ color: '#a0aec0' }}>Payout</span>
                <span style={{ fontWeight: '700', fontFamily: 'monospace', color: '#ffffff' }}>
                  ${payout1.toFixed(2)}
                </span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '12px',
                fontSize: '14px'
              }}>
                <span style={{ color: '#a0aec0' }}>Stake</span>
                <span style={{ fontWeight: '700', fontFamily: 'monospace', color: '#ffffff' }}>
                  ${stake1.toFixed(2)}
                </span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '12px',
                fontSize: '14px'
              }}>
                <span style={{ color: '#a0aec0' }}>Profit</span>
                <span style={{ fontWeight: '700', fontFamily: 'monospace', color: getTextColor(profit1) }}>
                  {profit1 > 0 ? '+' : ''}${profit1.toFixed(2)}
                </span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '14px'
              }}>
                <span style={{ color: '#a0aec0' }}>Conversion %</span>
                <span style={{ fontWeight: '700', fontFamily: 'monospace', color: getTextColor(conversion1) }}>
                  {conversion1.toFixed(2)}%
                </span>
              </div>
            </div>

            {/* If Hedge Bet Wins */}
            <div style={{
              backgroundColor: '#0f1419',
              padding: '24px',
              borderRadius: '10px',
              border: `3px solid ${getBorderColor(profit2)}`,
              color: '#ffffff'
            }}>
              <div style={{
                textAlign: 'center',
                marginBottom: '20px',
                fontSize: '14px',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                If Hedge Bet Wins
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '12px',
                fontSize: '14px'
              }}>
                <span style={{ color: '#a0aec0' }}>Payout</span>
                <span style={{ fontWeight: '700', fontFamily: 'monospace', color: '#ffffff' }}>
                  ${payout2.toFixed(2)}
                </span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '12px',
                fontSize: '14px'
              }}>
                <span style={{ color: '#a0aec0' }}>Stake</span>
                <span style={{ fontWeight: '700', fontFamily: 'monospace', color: '#ffffff' }}>
                  ${stake2.toFixed(2)}
                </span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '12px',
                fontSize: '14px'
              }}>
                <span style={{ color: '#a0aec0' }}>Profit</span>
                <span style={{ fontWeight: '700', fontFamily: 'monospace', color: getTextColor(profit2) }}>
                  {profit2 > 0 ? '+' : ''}${profit2.toFixed(2)}
                </span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '14px'
              }}>
                <span style={{ color: '#a0aec0' }}>Conversion %</span>
                <span style={{ fontWeight: '700', fontFamily: 'monospace', color: getTextColor(conversion2) }}>
                  {conversion2.toFixed(2)}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PromoConversionCalculator;
